import { Injectable } from "@angular/core";
import { DashboardMenuItem } from "../models/dashboard-menu-item";
import { DashboardItemType } from "../models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../../golden-layout/golden-layout-addition-mode";
import { BfxBaseMenuService } from "./bfx-base-menu.service";
import { SystemConfigPromiseService } from "shared/cubiq-services";
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import { SysFeatures } from "shared/cubiq-services";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { OESettingsService } from "app/order-entry/services/basic/oe-settings.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxNewOrderMenuService extends BfxBaseMenuService {
  private canEnterSpotOrder: boolean = false;
  private canEnterAlgoOrder: boolean = false;
  private fwdEnabled: boolean = false;
  private canEnterNdfOrder: boolean = false;
  private canEnterBmarkOrder: boolean = false;
  private canEntryGammaOrder: boolean = false;
  private canEnterNdfAlgoOrder: boolean = false;

  constructor(
    private goldenLayoutService: GoldenLayoutService,
    private systemConfigService: SystemConfigPromiseService,
    private oeSettings: OESettingsService,
    private translationService: TranslateService,
  ) {
    super();
    this.baseGoldenLayout = goldenLayoutService;
  }

  getMenuProvider(): Promise<Array<DashboardMenuItem>> {
    //mocked
    return new Promise<Array<DashboardMenuItem>>((res, rej) => {
      Promise.all([
        this.systemConfigService.canEnterOrder(),
        this.systemConfigService.canEnterAlgoOrder(),
        this.systemConfigService.getSystemConfig(SysFeatures.fwdEnabled),
        this.systemConfigService.canEnterNdfOrder(),
        this.systemConfigService.canEnterBmarkOrder(),
        this.systemConfigService.canEnterGammaOrder(),
        this.systemConfigService.canEnterNdfAlgoOrder(),
      ])
        .then(
          ([
            canEnterSpotOrder,
            canEnterAlgoOrder,
            fwdEnabled,
            canEnterNdfOrder,
            canEnterBmarkOrder,
            canEntryGammaOrder,
            canEnterNdfAlgoOrder,
          ]) => {
            this.canEnterSpotOrder = canEnterSpotOrder;
            this.canEnterAlgoOrder = canEnterAlgoOrder;
            this.fwdEnabled = fwdEnabled?.toUpperCase() == "Y";
            this.canEnterNdfOrder = canEnterNdfOrder;
            this.canEnterBmarkOrder = canEnterBmarkOrder;
            this.canEntryGammaOrder = canEntryGammaOrder;
            this.canEnterNdfAlgoOrder = canEnterNdfAlgoOrder;
            res(this.constructMenu());
          },
          (rejection) => {
            logger.out.error("ERROR:rejection BfxNewOrderMenuService:", rejection);
            res(null);
          },
        )
        .catch((reason) => {
          logger.out.error("ERROR:reason BfxNewOrderMenuService: ", reason);
          res(null);
        });
    });
  }

  constructMenu(): Array<DashboardMenuItem> {
    let menuItems: DashboardMenuItem[] = [];
    // SPOT ENTRY
    if (this.canEnterSpotOrder) {
      let title: string = this.fwdEnabled
        ? this.translationService.instant("order_entry.spotFwd.title")
        : this.translationService.instant("order_entry.spot.title");
      let config = this.oeSettings.getWindowPopoutConfiguration();
      config.data = {
        refId: null,
        loadDefaults: true,
        mode: OENOperationMode.NEW,
        entryType: OENEntryType.SPOT,
      };
      menuItems.push(
        this.createMenuItemWithId(
          "spot",
          title,
          DashboardItemType.NewOrder,
          GoldenLayoutAdditionMode.DEFAULT,
          true,
          config,
        ),
      );
    }
    // NDF ENTRY
    if (this.canEnterNdfOrder) {
      let title: string = this.translationService.instant("order_entry.ndf.title");
      let config = this.oeSettings.getWindowPopoutConfiguration();
      config.data = {
        refId: null,
        loadDefaults: true,
        mode: OENOperationMode.NEW,
        entryType: OENEntryType.NDF,
      };
      menuItems.push(
        this.createMenuItemWithId(
          "ndf",
          title,
          DashboardItemType.NewOrder,
          GoldenLayoutAdditionMode.DEFAULT,
          true,
          config,
        ),
      );
    }

    // GAMMA ENTRY
    if (this.canEntryGammaOrder) {
      let title: string = this.translationService.instant("order_entry.gamma.title");
      let config = this.oeSettings.getWindowPopoutConfiguration();
      config.data = {
        refId: null,
        loadDefaults: true,
        mode: OENOperationMode.NEW,
        entryType: OENEntryType.GAMMA,
      };
      menuItems.push(
        this.createMenuItemWithId(
          "gamma",
          title,
          DashboardItemType.NewOrder,
          GoldenLayoutAdditionMode.DEFAULT,
          true,
          config,
        ),
      );
    }

    // ALGO ENTRY
    if (this.canEnterAlgoOrder) {
      let title: string = this.translationService.instant("order_entry.algo.title");
      let config = this.oeSettings.getWindowPopoutConfiguration();
      config.data = {
        refId: null,
        loadDefaults: true,
        mode: OENOperationMode.NEW,
        entryType: OENEntryType.ALGO,
      };
      menuItems.push(
        this.createMenuItemWithId(
          "algo",
          title,
          DashboardItemType.NewOrder,
          GoldenLayoutAdditionMode.DEFAULT,
          true,
          config,
        ),
      );
    }
    // ALGO NDF ENTRY
    if (this.canEnterNdfAlgoOrder) {
      let title: string = this.translationService.instant("order_entry.ndfAlgo.title");
      let config = this.oeSettings.getWindowPopoutConfiguration();
      config.data = {
        refId: null,
        loadDefaults: true,
        mode: OENOperationMode.NEW,
        entryType: OENEntryType.NDF_ALGO,
      };
      menuItems.push(
        this.createMenuItemWithId(
          "ndfAlgo",
          title,
          DashboardItemType.NewOrder,
          GoldenLayoutAdditionMode.DEFAULT,
          true,
          config,
        ),
      );
    }
    // BMARK ENTRY
    if (this.canEnterBmarkOrder) {
      let title: string = this.translationService.instant("order_entry.bmark.title");
      let config = this.oeSettings.getWindowPopoutConfiguration();
      config.data = {
        refId: null,
        loadDefaults: true,
        mode: OENOperationMode.NEW,
        entryType: OENEntryType.BENCHMARK,
      };
      menuItems.push(
        this.createMenuItemWithId(
          "bmark",
          title,
          DashboardItemType.NewOrder,
          GoldenLayoutAdditionMode.DEFAULT,
          true,
          config,
        ),
      );
    }

    return menuItems;
  }
}
